import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Grid,
  Avatar,
  TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SettingsIcon from "@mui/icons-material/Settings";
import TrophyIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import RefreshIcon from "@mui/icons-material/Refresh";

import { getLocalStorageItem } from "hooks";
import {
  useGetPointsConfig,
  useUpdatePointsConfig,
  useGetMilestones,
  useCreateMilestone,
  useUpdateMilestone,
  useDeleteMilestone,
  useGetLeaderboard,
} from "./pointsQueries";

// ── Milestone Edit/Create Dialog ──────────────────────────────────────────────
const MilestoneDialog = ({ open, onClose, initial }) => {
  const isEdit = Boolean(initial);
  const { mutate: create, isLoading: creating } = useCreateMilestone();
  const { mutate: update, isLoading: updating } = useUpdateMilestone();

  const [form, setForm] = useState({
    entriesRequired: "",
    rankName: "",
    rewardTier: "",
    badgeIcon: "",
  });

  useEffect(() => {
    if (initial) {
      setForm({
        entriesRequired: initial.entriesRequired,
        rankName: initial.rankName,
        rewardTier: initial.rewardTier,
        badgeIcon: initial.badgeIcon || "",
      });
    } else {
      setForm({
        entriesRequired: "",
        rankName: "",
        rewardTier: "",
        badgeIcon: "",
      });
    }
  }, [initial, open]);

  const handleSave = () => {
    const entries = parseInt(form.entriesRequired, 10);
    if (isNaN(entries) || !form.rankName.trim() || !form.rewardTier.trim()) return;

    const payload = {
      entriesRequired: entries,
      rankName: form.rankName,
      rewardTier: form.rewardTier,
      badgeIcon: form.badgeIcon,
    };

    if (isEdit) {
      update({ id: initial.id, ...payload }, { onSuccess: onClose });
    } else {
      create(payload, { onSuccess: onClose });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? "Edit Milestone" : "Add Milestone"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1.5 }}>
          <TextField
            label="Entries Required"
            type="number"
            fullWidth
            value={form.entriesRequired}
            onChange={(e) => setForm((p) => ({ ...p, entriesRequired: e.target.value }))}
            disabled={isEdit}
          />
          <TextField
            label="Rank/Title Name (e.g. Gold Star)"
            fullWidth
            value={form.rankName}
            onChange={(e) => setForm((p) => ({ ...p, rankName: e.target.value }))}
          />
          <TextField
            label="Reward Tier (e.g. Level 3)"
            fullWidth
            value={form.rewardTier}
            onChange={(e) => setForm((p) => ({ ...p, rewardTier: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={creating || updating || !form.entriesRequired || !form.rankName.trim() || !form.rewardTier.trim()}
          sx={{ bgcolor: "#3D3E78", "&:hover": { bgcolor: "#2c2d58" } }}
        >
          {creating || updating ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Milestone Delete Confirm Dialog ───────────────────────────────────────────
const DeleteMilestoneDialog = ({ open, onClose, milestone }) => {
  const { mutate: del, isLoading } = useDeleteMilestone();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Milestone?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Are you sure you want to delete the milestone for <strong>{milestone?.entriesRequired} entries</strong> (
          {milestone?.rankName})?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={isLoading}
          onClick={() => del(milestone.id, { onSuccess: onClose })}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const PointsRewards = () => {
  const userRole = getLocalStorageItem("USER_ROLE");
  const isAdmin = userRole === "SUPER_ADMIN" || userRole === "ADMIN";

  const [tabValue, setTabValue] = useState(0);

  // Leaderboard filters & pagination
  const [filters, setFilters] = useState({
    factory: "",
    unit: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState({
    factory: "",
    unit: "",
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset page on search change
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(0); // Reset page on filter change
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  // Query parameters
  const leaderboardParams = {
    page: page + 1,
    perPage,
    ...(debouncedFilters.factory && { factory: debouncedFilters.factory }),
    ...(debouncedFilters.unit && { unit: debouncedFilters.unit }),
    ...(debouncedSearch && { search: debouncedSearch }),
  };

  // Queries
  const { data: leaderboardRes, isLoading: leaderboardLoading, refetch: refetchLeaderboard } = useGetLeaderboard(leaderboardParams);
  const { data: configRes, isLoading: configLoading } = useGetPointsConfig();
  const { data: milestonesRes, isLoading: milestonesLoading } = useGetMilestones();

  // Mutations
  const { mutate: updateConfig, isLoading: updatingConfig } = useUpdatePointsConfig();

  // State for config inputs
  const [configForm, setConfigForm] = useState({
    onAdd: "",
    onEdit: "",
    onDelete: "",
    regularityWeight: 0.7,
    entriesWeight: 0.3,
  });

  useEffect(() => {
    if (configRes?.data) {
      setConfigForm({
        onAdd: configRes.data.onAdd,
        onEdit: configRes.data.onEdit,
        onDelete: configRes.data.onDelete,
        regularityWeight: configRes.data.regularityWeight,
        entriesWeight: configRes.data.entriesWeight,
      });
    }
  }, [configRes]);

  // Dialog states
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [deletingMilestone, setDeletingMilestone] = useState(null);

  const handleSaveConfig = () => {
    updateConfig({
      onAdd: parseInt(configForm.onAdd, 10),
      onEdit: parseInt(configForm.onEdit, 10),
      onDelete: parseInt(configForm.onDelete, 10),
      regularityWeight: parseFloat(configForm.regularityWeight),
      entriesWeight: parseFloat(configForm.entriesWeight),
    });
  };

  const handleWeightChange = (field, value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    const clamped = Math.max(0, Math.min(1, num));
    const opposite = parseFloat((1 - clamped).toFixed(2));
    if (field === "regularityWeight") {
      setConfigForm((p) => ({
        ...p,
        regularityWeight: clamped,
        entriesWeight: opposite,
      }));
    } else {
      setConfigForm((p) => ({
        ...p,
        entriesWeight: clamped,
        regularityWeight: opposite,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({ factory: "", unit: "" });
    setDebouncedFilters({ factory: "", unit: "" });
    setSearch("");
    setDebouncedSearch("");
    setPage(0);
  };

  const filteredRecords = leaderboardRes?.data?.records || [];

  const totalRecords = leaderboardRes?.data?.meta?.total ?? 0;

  return (
    <Box className="w-[95%] pt-6 mx-auto self-start" sx={{ pb: 6 }}>
      {/* ── Title Area ────────────────────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#3D3E78" }}>
            Points & Rewards Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor worker performance, volume, consistency, and configure game mechanics.
          </Typography>
        </Box>
      </Stack>

      {/* ── Tabs ──────────────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{
            "& .MuiTab-root": { fontWeight: 600, color: "text.secondary" },
            "& .Mui-selected": { color: "#3D3E78 !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#3D3E78" },
          }}
        >
          <Tab icon={<EmojiEventsIcon fontSize="small" />} iconPosition="start" label="Leaderboard" />
          {isAdmin && (
            <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="System Configuration" />
          )}
        </Tabs>
      </Box>

      {/* ── Tab 1: Leaderboard ────────────────────────────────────────────────── */}
      {tabValue === 0 && (
        <Stack spacing={3}>
          {/* Filters Card */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: "#F7F9FC",
              borderColor: "grey.200",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Search Name / ID"
                  placeholder="Type to filter..."
                  fullWidth
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ bgcolor: "white", borderRadius: 1.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Factory"
                  placeholder="e.g. SHAHI"
                  fullWidth
                  size="small"
                  value={filters.factory}
                  onChange={(e) => setFilters((p) => ({ ...p, factory: e.target.value }))}
                  sx={{ bgcolor: "white", borderRadius: 1.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Unit"
                  placeholder="e.g. UNIT 1"
                  fullWidth
                  size="small"
                  value={filters.unit}
                  onChange={(e) => setFilters((p) => ({ ...p, unit: e.target.value }))}
                  sx={{ bgcolor: "white", borderRadius: 1.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleResetFilters}
                  startIcon={<RefreshIcon />}
                  sx={{
                    borderRadius: 2,
                    borderColor: "grey.300",
                    color: "text.primary",
                    textTransform: "none",
                    height: 40,
                  }}
                >
                  Reset Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Leaderboard Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "hidden", borderColor: "grey.200" }}
          >
            <Table>
              <TableHead sx={{ bgcolor: "#F0F2FA" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#3D3E78" }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#3D3E78" }}>Local Rank</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#3D3E78" }}>Worker Info</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#3D3E78" }}>Factory & Unit</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#3D3E78" }}>
                    Entries Volume
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#3D3E78" }}>
                    Regularity
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#3D3E78" }}>
                    Combined Score
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#3D3E78" }}>
                    Points Balance
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#3D3E78" }}>
                    Reward Tier / Rank Name
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Loading leaderboard data...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No workers found matching criteria.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((row) => {
                    // Trophy style colors
                    let rankBg = "transparent";
                    let rankColor = "text.primary";
                    let rankIcon = null;

                    if (row.globalRank === 1) {
                      rankBg = "#FFF9C4";
                      rankColor = "#F57F17";
                      rankIcon = <TrophyIcon sx={{ fontSize: 18, color: "#FBC02D", mr: 0.5 }} />;
                    } else if (row.globalRank === 2) {
                      rankBg = "#ECEFF1";
                      rankColor = "#455A64";
                      rankIcon = <TrophyIcon sx={{ fontSize: 18, color: "#B0BEC5", mr: 0.5 }} />;
                    } else if (row.globalRank === 3) {
                      rankBg = "#FFE0B2";
                      rankColor = "#E65100";
                      rankIcon = <TrophyIcon sx={{ fontSize: 18, color: "#FFB74D", mr: 0.5 }} />;
                    }

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          transition: "background-color 0.2s",
                          "&:hover": { bgcolor: "rgba(61, 62, 120, 0.04)" },
                        }}
                      >
                        <TableCell>
                          {rankBg !== "transparent" ? (
                            <Chip
                              icon={rankIcon}
                              label={`#${row.globalRank}`}
                              size="small"
                              sx={{
                                bgcolor: rankBg,
                                color: rankColor,
                                fontWeight: 750,
                                borderRadius: 1.5,
                              }}
                            />
                          ) : (
                            <Typography variant="body2" fontWeight={600} pl={1.5}>
                              #{row.globalRank}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            #{row.localRank}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: "#3D3E78",
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            >
                              {row.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {row.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {row.employeeId}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="column">
                            <Typography variant="body2" fontWeight={600}>
                              {row.factory}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.unit}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {row.totalEntries}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {row.regularity}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${row.combinedScore}`}
                            size="small"
                            sx={{
                              bgcolor: "#E8EBFA",
                              color: "#3D3E78",
                              fontWeight: 700,
                              minWidth: 50,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={row.points >= 0 ? "success.main" : "error.main"}
                          >
                            {row.points >= 0 ? `+${row.points}` : row.points}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Chip
                              icon={<StarIcon style={{ color: "#FBC02D" }} />}
                              label={row.rewardTier}
                              variant="outlined"
                              size="small"
                              sx={{ borderColor: "grey.300", fontWeight: 600 }}
                            />
                            <Typography variant="body2" fontWeight={600} color="text.secondary">
                              {row.rankName}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </Stack>
      )}

      {/* ── Tab 2: System Configuration ───────────────────────────────────────── */}
      {tabValue === 1 && isAdmin && (
        <Grid container spacing={4}>
          {/* Left Panel: Points & Weights Configuration */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: "grey.200" }}>
                <Typography variant="h6" fontWeight={700} mb={2.5} color="#3D3E78">
                  Transaction Points Settings
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    label="Points on Transaction Add (Manual)"
                    type="number"
                    fullWidth
                    value={configForm.onAdd}
                    onChange={(e) => setConfigForm((p) => ({ ...p, onAdd: e.target.value }))}
                    helperText="Points awarded when a worker adds an entry"
                  />
                  <TextField
                    label="Points on Transaction Edit (Penalty)"
                    type="number"
                    fullWidth
                    value={configForm.onEdit}
                    onChange={(e) => setConfigForm((p) => ({ ...p, onEdit: e.target.value }))}
                    helperText="Points deducted/added when a transaction is modified (use negative for deduction)"
                  />
                  <TextField
                    label="Points on Transaction Delete (Penalty)"
                    type="number"
                    fullWidth
                    value={configForm.onDelete}
                    onChange={(e) => setConfigForm((p) => ({ ...p, onDelete: e.target.value }))}
                    helperText="Points deducted/added when a transaction is deleted (use negative for deduction)"
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: "grey.200" }}>
                <Typography variant="h6" fontWeight={700} mb={2.5} color="#3D3E78">
                  Leaderboard Weight Allocation
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Weights must sum up to exactly 1.0 (100%). Modifying weight distribution changes the combined score calculations on the leaderboard.
                </Typography>
                <Stack spacing={3}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" fontWeight={650}>
                        Regularity Weight (60% - 70% Recommended)
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {Math.round(configForm.regularityWeight * 100)}%
                      </Typography>
                    </Stack>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: 0.05, min: 0, max: 1 }}
                      value={configForm.regularityWeight}
                      onChange={(e) => handleWeightChange("regularityWeight", e.target.value)}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" fontWeight={650}>
                        Entries Volume Weight (30% - 40% Recommended)
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        {Math.round(configForm.entriesWeight * 100)}%
                      </Typography>
                    </Stack>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ step: 0.05, min: 0, max: 1 }}
                      value={configForm.entriesWeight}
                      onChange={(e) => handleWeightChange("entriesWeight", e.target.value)}
                    />
                  </Box>
                </Stack>
              </Paper>

              <Button
                variant="contained"
                onClick={handleSaveConfig}
                disabled={updatingConfig || configLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2.5,
                  bgcolor: "#3D3E78",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#2c2d58" },
                }}
              >
                {updatingConfig ? "Saving configuration..." : "Save System Config"}
              </Button>
            </Stack>
          </Grid>

          {/* Right Panel: Reward Milestones Configuration */}
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: "grey.200" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                <Box>
                  <Typography variant="h6" fontWeight={700} color="#3D3E78">
                    Entry Volume Milestones
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define levels and badges unlocked at various entry counts.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingMilestone(null);
                    setMilestoneOpen(true);
                  }}
                  sx={{
                    borderColor: "#3D3E78",
                    color: "#3D3E78",
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    "&:hover": { borderColor: "#2c2d58", bgcolor: "rgba(61, 62, 120, 0.04)" },
                  }}
                >
                  Add Milestone
                </Button>
              </Stack>

              <TableContainer component={Box} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F5F6FA" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 650 }}>Entries Required</TableCell>
                      <TableCell sx={{ fontWeight: 650 }}>Reward Tier / Rank Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 650 }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestonesLoading ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          Loading milestones...
                        </TableCell>
                      </TableRow>
                    ) : milestonesRes?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          No reward milestones configured yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      milestonesRes.data.map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{m.entriesRequired.toLocaleString()}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={m.rewardTier} size="small" sx={{ bgcolor: "#ECF6FF", color: "#1976D2", fontWeight: 600 }} />
                              <Typography variant="body2" fontWeight={650}>
                                {m.rankName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setEditingMilestone(m);
                                  setMilestoneOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeletingMilestone(m)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Dialogs */}
          <MilestoneDialog
            open={milestoneOpen}
            onClose={() => setMilestoneOpen(false)}
            initial={editingMilestone}
          />
          <DeleteMilestoneDialog
            open={Boolean(deletingMilestone)}
            onClose={() => setDeletingMilestone(null)}
            milestone={deletingMilestone}
          />
        </Grid>
      )}
    </Box>
  );
};

export default PointsRewards;
